// Authored content for the closing lesson — CD04's V4516, "Where to go next and
// how to keep learning".
//
// WHY THIS IS AUTHORED RATHER THAN GENERATED
//
// The generator writes every rubric the same way: five criteria, each demanding a
// separate thing from the transcript. On the last page of the course that produced
// a rubric asking the student to name an interest, ask for *specific, actionable*
// advice, extract *two concrete next steps*, engage with follow-up questions, AND
// demonstrate an understanding that learning comes from building — five gates on a
// lesson that teaches nothing new and grades nobody's project. A student who says
// what they want to build next and gets an answer had done the thing this lesson
// is for, and could still fail it.
//
// So the bar here is deliberately ONE line: say what you want to pursue next. This
// lesson closes the course; it is not the place to lose someone.
//
// ONE criterion is also a scoring decision, not just brevity. The judge passes at
// 80% of criteria (buildJudgeSystemPrompt in backend ClaudeCourseService.js), so
// with two criteria a student would need both anyway — a second "nice to have"
// line could only ever be a second gate. If this rubric ever grows, every line
// added has to be one a passing student always satisfies.
//
// The hub overrides `definition.rubric`, `exercisePrompt` and `hint` on read — the
// same precedent as appIdeaLesson.js and buildSpecLesson.js. Overriding on read
// means no cache purge and no DEF_VERSION bump, so students keep their cached
// prose. `lessonMarkdown` is deliberately NOT overridden: it teaches how to keep
// learning after the course, which is unchanged, and its pregenerated ElevenLabs
// narration is keyed to that exact text.
//
// Only CD04 is listed. The other courses have the same closing lesson (CD01's
// CD505, CD02's V2505, CD03's V3506) and the same generated rubric — add their ids
// here if that one-line bar should apply to them too.

/** The lessons this file drives. */
export const FINALE_LESSON_IDS = ["V4516"];

export const isFinaleLesson = (lessonID) => FINALE_LESSON_IDS.includes(lessonID);

const COPY = {
    en: {
        // Written to pass on a stated intention in the student's own words. It asks
        // for a direction, not a plan: "I want to add login to my app" passes, and
        // so does "I want to keep going with Claude Code".
        rubric: [
            "You told Claude what you want to learn, build, or work on next.",
        ],
        exercise: [
            "Tell Claude what you want to do next — a feature you'd like to add to your app, something you'd like to build, or a part of development you want to understand better. One or two sentences is enough.",
            "",
            "Then ask what you should learn first, and keep the conversation going as long as it's useful. That part isn't graded — say where you're headed and this lesson is done.",
        ].join("\n"),
        hint:
            "You don't need a five-year plan. Pick the next thing that would make your app better and start there.\n\nIf nothing comes to mind, paste this to Claude: **\"Here's the app I built in this course. What's one feature I could add next that would teach me something new?\"**",
    },
    es: {
        rubric: [
            "Le dijiste a Claude qué quieres aprender, construir o trabajar a continuación.",
        ],
        exercise: [
            "Dile a Claude qué quieres hacer ahora — una función que te gustaría agregarle a tu app, algo que quieras construir o una parte del desarrollo que quieras entender mejor. Con una o dos frases basta.",
            "",
            "Después pregúntale qué deberías aprender primero y sigue la conversación mientras te sirva. Esa parte no se califica — con decir hacia dónde vas, esta lección está lista.",
        ].join("\n"),
        hint:
            "No necesitas un plan de cinco años. Elige lo siguiente que haría mejor tu app y empieza por ahí.\n\nSi no se te ocurre nada, pégale esto a Claude: **\"Esta es la app que construí en este curso. ¿Qué función podría agregarle ahora que me enseñe algo nuevo?\"**",
    },
};

const copyFor = (locale) => (locale === "es" ? "es" : "en");

/** The lesson's rubric — one line, on purpose. See the note at the top. */
export const finaleRubric = (locale) => [...COPY[copyFor(locale)].rubric];

/** The exercise prompt and hint shown in the lesson pane. */
export const finaleExercise = (locale) => {
    const copy = COPY[copyFor(locale)];
    return { exercisePrompt: copy.exercise, hint: copy.hint };
};
